import * as XLSX from 'xlsx';

export interface ExcelAnalysisResult {
  documentType: DocumentType;
  metrics: ExcelMetrics;
  selectedAgent: string;
  dataPreview: any[];
  enrichedQuery: string;
  rowCount: number;
  columnCount: number;
}

export type DocumentType =
  | 'aging_report'
  | 'invoice_list'
  | 'payment_schedule'
  | 'cash_flow'
  | 'ar_summary'
  | 'ap_summary'
  | 'general_ledger'
  | 'unknown';

export interface ExcelMetrics {
  totals?: Record<string, number>;
  dateRange?: { start: Date; end: Date };
  summary?: string;
  keyInsights?: string[];
}

/**
 * Main entry point for Excel file processing
 */
export async function processExcel(
  file: File,
  query: string,
  context: any
): Promise<ExcelAnalysisResult> {
  try {
    // Read the Excel file
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    // Get the first sheet
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    // Convert to JSON
    const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (data.length === 0) {
      throw new Error('Excel file is empty');
    }

    // Extract headers (first row)
    const headers = data[0] as string[];
    const rows = data.slice(1);

    // Detect document type
    const documentType = detectDocumentType(headers, rows);

    // Extract metrics based on document type
    const metrics = extractMetrics(headers, rows, documentType);

    // Select appropriate agent
    const selectedAgent = selectAgentForExcel(documentType, query);

    // Create enriched query with Excel context
    const enrichedQuery = enrichQueryWithExcelData(
      query,
      documentType,
      metrics,
      headers,
      rows.length
    );

    // Get data preview (first 10 rows)
    const dataPreview = rows.slice(0, 10).map(row => {
      const obj: any = {};
      headers.forEach((header, idx) => {
        obj[header] = row[idx];
      });
      return obj;
    });

    return {
      documentType,
      metrics,
      selectedAgent,
      dataPreview,
      enrichedQuery,
      rowCount: rows.length,
      columnCount: headers.length
    };
  } catch (error) {
    console.error('Excel processing error:', error);
    throw new Error(`Failed to process Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Detect the type of financial document based on headers and data patterns
 */
export function detectDocumentType(
  headers: string[],
  data: any[][]
): DocumentType {
  const headerStr = headers.join(' ').toLowerCase();

  // Aging Report: customer, invoice, due date, aging buckets
  if (/aging|overdue|past due|0-30|31-60|61-90|90\+/.test(headerStr) &&
      /customer|client|account/.test(headerStr)) {
    return 'aging_report';
  }

  // Invoice List: invoice number, date, amount, customer
  if (/invoice|inv#|invoice number/.test(headerStr) &&
      /amount|total|balance/.test(headerStr) &&
      /customer|client/.test(headerStr)) {
    return 'invoice_list';
  }

  // Payment Schedule: payment date, due date, amount
  if (/payment|due date|scheduled/.test(headerStr) &&
      /amount|payment amount/.test(headerStr)) {
    return 'payment_schedule';
  }

  // Cash Flow: date, inflow, outflow, balance
  if (/cash|flow|balance/.test(headerStr) &&
      (/inflow|outflow|receipts|disbursements/.test(headerStr) ||
       /debit|credit/.test(headerStr))) {
    return 'cash_flow';
  }

  // AR Summary: receivables, collections, outstanding
  if (/receivable|ar|collection/.test(headerStr) &&
      /outstanding|balance|amount/.test(headerStr)) {
    return 'ar_summary';
  }

  // AP Summary: payables, vendor, outstanding
  if (/payable|ap|vendor|supplier/.test(headerStr) &&
      /outstanding|amount|due/.test(headerStr)) {
    return 'ap_summary';
  }

  // General Ledger: account, debit, credit, date
  if (/account|gl|ledger/.test(headerStr) &&
      /debit|credit/.test(headerStr) &&
      /date/.test(headerStr)) {
    return 'general_ledger';
  }

  return 'unknown';
}

/**
 * Extract key metrics from the Excel data based on document type
 */
export function extractMetrics(
  headers: string[],
  data: any[][],
  type: DocumentType
): ExcelMetrics {
  const metrics: ExcelMetrics = {};

  try {
    switch (type) {
      case 'aging_report':
        metrics.totals = extractAgingTotals(headers, data);
        metrics.summary = generateAgingSummary(metrics.totals);
        break;

      case 'invoice_list':
        metrics.totals = extractInvoiceTotals(headers, data);
        metrics.dateRange = extractDateRange(headers, data);
        break;

      case 'payment_schedule':
        metrics.totals = extractPaymentTotals(headers, data);
        metrics.dateRange = extractDateRange(headers, data);
        break;

      case 'cash_flow':
        metrics.totals = extractCashFlowTotals(headers, data);
        metrics.summary = generateCashFlowSummary(metrics.totals);
        break;

      case 'ar_summary':
      case 'ap_summary':
        metrics.totals = extractFinancialTotals(headers, data);
        break;

      default:
        // For unknown types, try to find any numeric columns and sum them
        metrics.totals = extractGenericTotals(headers, data);
    }

    // Generate key insights
    metrics.keyInsights = generateKeyInsights(type, metrics, data.length);

  } catch (error) {
    console.error('Metrics extraction error:', error);
    metrics.summary = 'Unable to extract detailed metrics from this file';
  }

  return metrics;
}

/**
 * Select the appropriate AI agent based on document type and query
 */
export function selectAgentForExcel(
  type: DocumentType,
  query: string
): string {
  const queryLower = query.toLowerCase();

  // Check query intent first
  if (/help|how|what|guide/.test(queryLower)) {
    return 'Zephyr';
  }

  if (/risk|credit|default/.test(queryLower)) {
    return 'Levante';
  }

  // Use document type to determine agent
  switch (type) {
    case 'aging_report':
    case 'ar_summary':
      return /cash|pay|stabiliz/.test(queryLower) ? 'Poniente' : 'Levante';

    case 'payment_schedule':
    case 'cash_flow':
      return 'Poniente';

    case 'ap_summary':
      return 'Poniente';

    case 'invoice_list':
      return /quote|sales|revenue/.test(queryLower) ? 'Full Send' : 'Windrose';

    case 'general_ledger':
      return 'Windrose';

    default:
      return 'Windrose'; // Default to analytics
  }
}

// Helper functions for metric extraction

function extractAgingTotals(headers: string[], data: any[][]): Record<string, number> {
  const totals: Record<string, number> = {};

  headers.forEach((header, idx) => {
    const headerLower = header.toLowerCase();
    if (/amount|total|balance|0-30|31-60|61-90|90\+|current|overdue/.test(headerLower)) {
      const sum = data.reduce((acc, row) => {
        const value = parseFloat(row[idx]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0);
      totals[header] = sum;
    }
  });

  return totals;
}

function extractInvoiceTotals(headers: string[], data: any[][]): Record<string, number> {
  const totals: Record<string, number> = {};

  headers.forEach((header, idx) => {
    const headerLower = header.toLowerCase();
    if (/amount|total|balance|paid|outstanding/.test(headerLower)) {
      const sum = data.reduce((acc, row) => {
        const value = parseFloat(row[idx]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0);
      totals[header] = sum;
    }
  });

  totals['Invoice Count'] = data.length;

  return totals;
}

function extractPaymentTotals(headers: string[], data: any[][]): Record<string, number> {
  const totals: Record<string, number> = {};

  headers.forEach((header, idx) => {
    const headerLower = header.toLowerCase();
    if (/amount|payment|total/.test(headerLower)) {
      const sum = data.reduce((acc, row) => {
        const value = parseFloat(row[idx]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0);
      totals[header] = sum;
    }
  });

  return totals;
}

function extractCashFlowTotals(headers: string[], data: any[][]): Record<string, number> {
  const totals: Record<string, number> = {};

  headers.forEach((header, idx) => {
    const headerLower = header.toLowerCase();
    if (/inflow|outflow|receipt|disbursement|debit|credit|balance/.test(headerLower)) {
      const sum = data.reduce((acc, row) => {
        const value = parseFloat(row[idx]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0);
      totals[header] = sum;
    }
  });

  return totals;
}

function extractFinancialTotals(headers: string[], data: any[][]): Record<string, number> {
  const totals: Record<string, number> = {};

  headers.forEach((header, idx) => {
    const headerLower = header.toLowerCase();
    if (/amount|total|balance|outstanding|current|overdue/.test(headerLower)) {
      const sum = data.reduce((acc, row) => {
        const value = parseFloat(row[idx]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0);
      totals[header] = sum;
    }
  });

  return totals;
}

function extractGenericTotals(headers: string[], data: any[][]): Record<string, number> {
  const totals: Record<string, number> = {};

  headers.forEach((header, idx) => {
    // Try to sum numeric columns
    const firstValue = data[0]?.[idx];
    if (typeof firstValue === 'number' || !isNaN(parseFloat(firstValue))) {
      const sum = data.reduce((acc, row) => {
        const value = parseFloat(row[idx]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0);
      if (sum !== 0) {
        totals[header] = sum;
      }
    }
  });

  return totals;
}

function extractDateRange(headers: string[], data: any[][]): { start: Date; end: Date } | undefined {
  const dateColIdx = headers.findIndex(h =>
    /date|invoice date|due date|payment date/.test(h.toLowerCase())
  );

  if (dateColIdx === -1) return undefined;

  const dates = data
    .map(row => new Date(row[dateColIdx]))
    .filter(date => !isNaN(date.getTime()));

  if (dates.length === 0) return undefined;

  return {
    start: new Date(Math.min(...dates.map(d => d.getTime()))),
    end: new Date(Math.max(...dates.map(d => d.getTime())))
  };
}

function generateAgingSummary(totals: Record<string, number>): string {
  const total = Object.values(totals).reduce((a, b) => a + b, 0);
  const overdue = Object.entries(totals)
    .filter(([key]) => /overdue|past due|31-60|61-90|90\+/.test(key.toLowerCase()))
    .reduce((sum, [_, val]) => sum + val, 0);

  const overduePercent = total > 0 ? ((overdue / total) * 100).toFixed(1) : '0';

  return `Total AR: $${total.toLocaleString()} | ${overduePercent}% overdue`;
}

function generateCashFlowSummary(totals: Record<string, number>): string {
  const inflow = totals['Inflow'] || totals['Receipts'] || 0;
  const outflow = totals['Outflow'] || totals['Disbursements'] || 0;
  const net = inflow - outflow;

  return `Net Cash Flow: $${net.toLocaleString()} (In: $${inflow.toLocaleString()}, Out: $${outflow.toLocaleString()})`;
}

function generateKeyInsights(
  type: DocumentType,
  metrics: ExcelMetrics,
  rowCount: number
): string[] {
  const insights: string[] = [];

  insights.push(`Document contains ${rowCount} rows of data`);

  if (metrics.dateRange) {
    insights.push(`Date range: ${metrics.dateRange.start.toLocaleDateString()} to ${metrics.dateRange.end.toLocaleDateString()}`);
  }

  if (metrics.totals && Object.keys(metrics.totals).length > 0) {
    const maxTotal = Math.max(...Object.values(metrics.totals));
    insights.push(`Largest total: $${maxTotal.toLocaleString()}`);
  }

  return insights;
}

function enrichQueryWithExcelData(
  query: string,
  type: DocumentType,
  metrics: ExcelMetrics,
  headers: string[],
  rowCount: number
): string {
  let enriched = `${query}\n\n[Excel File Context]:\n`;
  enriched += `- Document Type: ${type.replace('_', ' ').toUpperCase()}\n`;
  enriched += `- Rows: ${rowCount}\n`;
  enriched += `- Columns: ${headers.join(', ')}\n`;

  if (metrics.summary) {
    enriched += `- Summary: ${metrics.summary}\n`;
  }

  if (metrics.totals) {
    enriched += `- Key Totals:\n`;
    Object.entries(metrics.totals).slice(0, 5).forEach(([key, value]) => {
      enriched += `  * ${key}: $${value.toLocaleString()}\n`;
    });
  }

  if (metrics.keyInsights) {
    enriched += `- Insights: ${metrics.keyInsights.join('; ')}\n`;
  }

  return enriched;
}
